import {
  type Cell,
  type Row,
  type Table,
  flexRender,
} from "@tanstack/react-table";
import { cn, getPinningStyles } from "../../lib/utils";
import { TableBody, TableCell, TableRow } from "../table";
import { DataTableEmptyRow } from "./data-table-empty-row";
import { TableLoadingRows } from "./loading-rows";

type DataTableCellProps<T> = {
  cell: Cell<T, unknown>;
};

function DataTableCell<T>({ cell }: DataTableCellProps<T>) {
  const isPinned =
    cell.column.getIsPinned() === "left" ||
    cell.column.getIsPinned() === "right";

  return (
    <TableCell
      style={{
        ...getPinningStyles(cell.column),
        width: cell.column.getSize(),
      }}
      className={cn(
        "group-hover:bg-blue-50 [&_button]:hover:border-secondary [&_button]:after:absolute [&_button]:after:content-[''] [&_button]:after:top-0 [&_button]:after:bottom-0 [&_button]:after:right-0 [&_button]:after:left-0",
        isPinned && "bg-white",
      )}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
}

type DataTableBodyProps<T extends { id: unknown }> = {
  table: Table<T>;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  testIdPrefix?: string;
  "data-testid"?: string;
};

export const DataTableBody = <T extends { id: unknown }>({
  table,
  onRowClick = () => {},
  isLoading = false,
  testIdPrefix,
  "data-testid": dataTestId,
}: DataTableBodyProps<T>) => {
  if (isLoading) {
    return <TableLoadingRows<T> table={table} />;
  }
  if (table.getRowModel().rows.length === 0) {
    return <DataTableEmptyRow<T> table={table} />;
  }

  const meta = table.options.meta as {
    getRowClassName?: (row: Row<T>) => string;
  };

  return (
    <TableBody data-testid={dataTestId}>
      {table.getRowModel().rows.map((row) => (
        <TableRow
          key={String(row.id)}
          className={cn("group", meta?.getRowClassName?.(row))}
          onClick={() => onRowClick(row.original)}
          data-testid={
            testIdPrefix ? `${testIdPrefix}-${row.original.id}` : undefined
          }
        >
          {row.getVisibleCells().map((cell) => (
            <DataTableCell key={cell.id} cell={cell} />
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
};
